import { COLORS } from "../../common/colors";
import type {
  Triage,
  OutreachSource
} from "src/providers/compliance/complianceTypes";
import type { ComplianceCheck } from "src/providers/compliance/complianceFilters";

// Okabe-Ito derived colorblind-safe palette
export const CB = {
  BLUE: "#0072B2",
  BLUE_LIGHT: "#56B4E9",
  BLUE_BG: "#e8f4fb",
  BLUE_TEXT: "#004a75",
  ORANGE: "#B87700",
  ORANGE_LIGHT: "#F5C754",
  ORANGE_BG: "#fef5e0",
  ORANGE_TEXT: "#7a5400",
  VERMILLION: "#D55E00",
  VERMILLION_LIGHT: "#E8956A",
  VERMILLION_BG: "#fceee5",
  VERMILLION_TEXT: "#8a3d00",
  SKY: "#2196C7",
  SKY_BG: "#eaf5fc",
  SKY_TEXT: "#1a6d9e"
} as const;

// Shape carries the state, so colours can be soft: a fill and a darker stroke of the same hue.
// Strokes clear 3:1 on white and stay distinct with colour-vision deficiencies.
export const SWATCH = {
  GOOD_FILL: "#7FCBB0",
  GOOD_STROKE: "#2E9A78",
  PARTIAL_FILL: "#F2CB6B",
  PARTIAL_STROKE: "#B98612",
  POOR_STROKE: "#C8424F",
  NEUTRAL_HATCH: "#C9D0D8",
  NEUTRAL_STROKE: "#8C96A3"
} as const;

const SWATCH_RADIUS = "5px";
const SWATCH_STROKE_WIDTH = "2px";

export const COMPLIANCE_COLORS = {
  BORDER_ROW: "#efefef",
  BORDER_SECTION: "#e4e9ef",
  HOVER_BG: "#f9fbff",
  ROW_ALT_BG: "#fbfbfb",
  PAGE_BG: "#eff4fa",
  CONTROL_BORDER: "#dce2ea"
} as const;

const FOCUSED_EDGE_SX = {
  content: '""',
  position: "absolute",
  top: 0,
  // Extends over the row's bottom border so the shadow is continuous between rows.
  bottom: "-1px",
  width: "8px",
  pointerEvents: "none"
} as const;

// Side shadows are drawn as full-height gradient strips: a box-shadow fades out at each
// cell's top and bottom, leaving gaps at every row divider.
export const FOCUSED_COLUMN_SX = {
  position: "relative",
  zIndex: 1,
  backgroundColor: "#ffffff",
  "&::before": {
    ...FOCUSED_EDGE_SX,
    left: "-8px",
    background: "linear-gradient(to left, rgba(16, 24, 40, 0.14), transparent)"
  },
  "&::after": {
    ...FOCUSED_EDGE_SX,
    right: "-8px",
    background: "linear-gradient(to right, rgba(16, 24, 40, 0.14), transparent)"
  }
} as const;

export function triageGroupColors(triage: Triage): {
  bg: string;
  fg: string;
  dot: string;
} {
  switch (triage) {
    case "out_of_compliance":
      return {
        bg: CB.VERMILLION_BG,
        fg: CB.VERMILLION_TEXT,
        dot: CB.VERMILLION
      };
    case "at_risk":
      return { bg: CB.ORANGE_BG, fg: CB.ORANGE_TEXT, dot: CB.ORANGE };
    // Grey like the cells it couldn't fill: a loading problem, not a compliance one.
    case "data_unavailable":
      return {
        bg: COLORS.GREY_100,
        fg: COLORS.GREY_800,
        dot: SWATCH.NEUTRAL_STROKE
      };
    case "steady":
      return { bg: CB.BLUE_BG, fg: CB.BLUE_TEXT, dot: CB.BLUE };
    case "unscoreable":
      return { bg: COLORS.GREY_100, fg: COLORS.GREY_800, dot: COLORS.GREY_400 };
    case "dnf":
      return { bg: "#f2f3f5", fg: COLORS.GREY_800, dot: "#6b7480" };
    case "done":
      return { bg: CB.SKY_BG, fg: CB.SKY_TEXT, dot: CB.SKY };
  }
}

export type SwatchLevel = "good" | "partial" | "poor" | "neutral" | "exempt";

// Cells are coloured from the same compliance check that drives triage, so a cell can't
// disagree with its patient's triage group or the filter card counts.
export function checkSwatchLevel(check: ComplianceCheck): SwatchLevel {
  switch (check.status) {
    case "pass":
      return "good";
    case "warn":
      return "partial";
    case "fail":
      return "poor";
    case "too_early":
    case "not_applicable":
      return "neutral";
  }
}

export const SWATCH_LEVEL_LABELS: Record<SwatchLevel, string> = {
  good: "on track",
  partial: "at risk",
  poor: "out of compliance",
  neutral: "not assessed",
  exempt: "exempt"
};

// full = good, half = at risk, empty = out of compliance, hatched = not assessed (yet),
// struck through = exempt. Exempt gets its own shape since, unlike not assessed, it won't
// change on its own.
export function swatchSx(level: SwatchLevel): Record<string, string> {
  const base = (stroke: string) => ({
    borderRadius: SWATCH_RADIUS,
    backgroundColor: "#ffffff",
    boxShadow: `inset 0 0 0 ${SWATCH_STROKE_WIDTH} ${stroke}`
  });
  switch (level) {
    case "good":
      return { ...base(SWATCH.GOOD_STROKE), backgroundColor: SWATCH.GOOD_FILL };
    case "partial":
      return {
        ...base(SWATCH.PARTIAL_STROKE),
        backgroundImage: `linear-gradient(to top, ${SWATCH.PARTIAL_FILL} 50%, transparent 50%)`
      };
    case "poor":
      return base(SWATCH.POOR_STROKE);
    case "neutral":
      return {
        ...base(SWATCH.NEUTRAL_STROKE),
        backgroundImage: `repeating-linear-gradient(45deg, ${SWATCH.NEUTRAL_HATCH} 0 1.5px, transparent 1.5px 5px)`
      };
    case "exempt":
      return {
        ...base(SWATCH.NEUTRAL_STROKE),
        backgroundImage: `linear-gradient(to top right, transparent calc(50% - 1px), ${SWATCH.NEUTRAL_STROKE} calc(50% - 1px) calc(50% + 1px), transparent calc(50% + 1px))`
      };
  }
}

export const CHART = {
  DATA_FILL: "#C2B9F2",
  DATA_STROKE: "#6250D6",
  DATA_LOW_FILL: "#F4A48F",
  DATA_LOW_STROKE: "#D2523A",
  TAKEN_FILL: SWATCH.GOOD_FILL,
  TAKEN_STROKE: SWATCH.GOOD_STROKE,
  SKIPPED_FILL: SWATCH.PARTIAL_FILL,
  SKIPPED_STROKE: SWATCH.PARTIAL_STROKE,
  OUTREACH_FILL: "#EBBBD6",
  OUTREACH_STROKE: "#B0478A"
} as const;

export interface ChartStyle {
  color: string;
  stroke: string;
  hatch?: string;
}

// Zendesk outreach is hatched so the two sources differ by more than colour.
export function outreachStyle(source: OutreachSource): ChartStyle {
  const base = { color: CHART.OUTREACH_FILL, stroke: CHART.OUTREACH_STROKE };
  return source === "zendesk"
    ? { ...base, hatch: CHART.OUTREACH_STROKE }
    : base;
}
